import { getInstance } from './index';
import Swal from 'sweetalert2';

// Detects whether the browser is blocking this iframe from accessing its own
// storage/cookies as a third party. When that's blocked, Auth0's silent SSO
// check (a hidden iframe call relying on Auth0's session cookie) fails and
// isAuthenticated() comes back false even though the user IS logged in
// elsewhere - so we must not report that as "please login" in that case.
// Feature-detected because the Storage Access API isn't available in every
// browser; when it's unavailable we can't distinguish the two cases, so we
// fall back to the original "please login" behavior rather than guessing.
// NOTE: this API requires a secure context (HTTPS) to behave per spec - it is
// unreliable on http://localhost, so only trust it in a real HTTPS deployment.
async function isThirdPartyStorageBlocked() {
  if (typeof document.hasStorageAccess !== 'function') {
    return false;
  }
  try {
    const hasAccess = await document.hasStorageAccess();
    return !hasAccess;
  } catch (e) {
    return false;
  }
}

function showPleaseLoginMessage() {
  Swal.fire({
      title: 'Please Login',
      text: 'Please login by clicking the button below.',
      confirmButtonText: "Log In",
      confirmButtonColor: "#ED0D87"
  }).then(() => {
      let url = window.location.protocol + "//" + window.location.host
      window.open(url, '_blank');
      Swal.fire({
          title: 'Refresh Page',
          text: "After logging in, please click the refresh button below.",
          confirmButtonText: "Refresh",
          confirmButtonColor: "#ED0D87"
      }).then(() => {
          window.location.reload();
      });
  });
}

// Browsers that block third-party storage (Safari's full third-party cookie
// block since 13.1, in particular) have no settings toggle that restores it -
// "Prevent Cross-Site Tracking" governs separate ITP heuristics, not this.
// The only sanctioned way back in is the Storage Access API's
// requestStorageAccess(), and it only works when called synchronously within
// a real user gesture - hence the button here rather than doing it
// automatically.
//
// The request is fired but NOT awaited to drive the UI: in practice its
// promise can grant access at the browser level while taking a long time (or
// never cleanly settling) to resolve, which made the button look like it did
// nothing even though access was actually granted. So we fire it and
// immediately move to an explicit refresh step instead, mirroring the
// login flow below.
function showStorageBlockedMessage() {
  Swal.fire({
    title: '<strong>Browser Settings Are Blocking This Window</strong>',
    html:
      'Your browser is blocking Message IQ from accessing your login session inside this window. ' +
      'Click below to grant access.',
    confirmButtonText: 'Enable Access',
    confirmButtonColor: '#ED0D87',
    showCloseButton: false,
    showCancelButton: false,
    focusConfirm: false
  }).then(() => {
    if (typeof document.requestStorageAccess === 'function') {
      // Fired within this click-driven .then(), still inside the user
      // gesture window. Best-effort only - see note above.
      document.requestStorageAccess().catch(() => {});
    }
    Swal.fire({
      title: 'Refresh Page',
      text: "Access should now be enabled. Please click the refresh button below.",
      confirmButtonText: "Refresh",
      confirmButtonColor: "#ED0D87"
    }).then(() => {
      window.location.reload();
    });
  });
}

export const authGuardIframe = (to, from, next) => {

  const authService = getInstance();

  const fn = async () => {
    // If the user is authenticated, continue with the route
    if (authService.isAuthenticated) {
      return next();
    }

    // isAuthenticated is false - figure out whether the user really isn't
    // logged in, or the browser blocked this iframe's storage access and
    // produced a false negative.
    if (await isThirdPartyStorageBlocked()) {
      showStorageBlockedMessage();
    } else {
      showPleaseLoginMessage();
    }
  };

  // If loading has already finished, check the auth state using `fn()`
  if (!authService.loading) {
    return fn();
  }

  // Watch for the loading property to change before checking isAuthenticated
  authService.$watch('loading', (loading) => {
    if (loading === false) {
      return fn();
    }
  });
};